import type { CatalogChange, Meta } from '@numistic/contracts'
import { useTranslation } from '@numistic/i18n'
import { Link } from '@tanstack/react-router'

import { formatDateTime } from '../lib/format.js'
import { displayValue } from '../meta/display.js'
import { useRefResolver } from '../meta/ref-resolver.js'
import { CATALOG_REF_KINDS } from './shared.js'

/** Журнал изменений общего каталога: кто, когда, какие поля и как изменились. */
export function ChangeList({
  meta,
  changes,
  withItem = false,
}: {
  meta: Meta
  changes: CatalogChange[]
  withItem?: boolean
}) {
  const { t } = useTranslation()
  const resolve = useRefResolver(meta, CATALOG_REF_KINDS)
  const defs = new Map(meta.fields.map((f) => [f.code, f]))
  const show = (code: string, value: unknown) => {
    if (value === null || value === undefined || value === '') return '—'
    const def = defs.get(code)
    if (def) return displayValue(meta, def, value, resolve) || '—'
    if (code === 'visibility')
      return t(`catalog.visibilities.${String(value)}` as 'catalog.visibilities.public')
    return typeof value === 'object' ? JSON.stringify(value) : String(value)
  }
  const label = (code: string) =>
    defs.get(code)?.name.ru ??
    t(`history.keys.${code}` as 'history.keys.label', { defaultValue: code })

  if (!changes.length) return <p className="text-sm text-muted-foreground">{t('history.empty')}</p>
  return (
    <ol className="grid gap-3">
      {changes.map((c) => {
        const pairs = Object.entries(c.changes).filter(
          (e): e is [string, [unknown, unknown]] => Array.isArray(e[1]) && e[1].length === 2,
        )
        const notes = Object.entries(c.changes).filter(([, v]) => !Array.isArray(v))
        return (
          <li key={c.id} className="grid gap-1 border-l-2 border-primary/40 pl-3 text-sm">
            <div className="flex flex-wrap items-baseline gap-x-2 text-muted-foreground">
              <span className="font-medium text-foreground">
                {t(`history.actions.${c.action}` as 'history.actions.update', {
                  defaultValue: c.action,
                })}
              </span>
              {withItem ? (
                <Link
                  to="/catalog/$id"
                  params={{ id: c.catalogItemId }}
                  className="text-primary underline underline-offset-4 hover:decoration-2"
                >
                  {c.itemTitle}
                </Link>
              ) : null}
              <span>{formatDateTime(c.createdAt)}</span>
              <span>{c.user?.name ?? t('history.unknownUser')}</span>
            </div>
            {notes.length ? (
              <span className="text-xs text-muted-foreground">
                {notes.map(([k, v]) => `${label(k)}: ${String(v)}`).join(' · ')}
              </span>
            ) : null}
            {pairs.length ? (
              <dl className="grid gap-0.5 text-xs sm:grid-cols-[12rem_1fr]">
                {pairs.map(([code, [before, after]]) => (
                  <div key={code} className="contents">
                    <dt className="text-muted-foreground">{label(code)}</dt>
                    <dd>
                      <span className="text-muted-foreground line-through">
                        {show(code, before)}
                      </span>{' '}
                      → {show(code, after)}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}
